<template>
    <div>
        <SToolbar>
            <v-icon small class="mr-1">ic-20 ic-service ic-col-grey</v-icon>
            <v-toolbar-title>{{ $t('service_pages') }}</v-toolbar-title>
        </SToolbar>
        <v-skeleton-loader v-if="!record" type="article, actions" />
        <v-card v-else height="var(--usable-height)" class="main-grid">
            <LoadingOverlay :value="saving" absolute />

            <!-- Header -->
            <div class="top-header">
                <light-toolbar dense>
                    <v-toolbar-title>
                        {{ $t('eintrag_bearbeiten') }} |
                        <strong v-if="record.is_published"
                            >{{ $t('publiziert') }}: {{ record.publish_from.formatIf('dd, DD.MM.YYYY') }}</strong
                        >
                        <strong v-else>{{ $t('nicht_publiziert') }}</strong>
                    </v-toolbar-title>
                </light-toolbar>
                <v-toolbar dense class="elevation-0" color="primary white--text">
                    <v-slide-group next-icon="ic-20 ic-next ic-col-white" prev-icon="ic-20 ic-prev ic-col-white">
                        <v-btn color="primary" depressed :disabled="!valid || saving" @click.stop="save">
                            <KpIcon icon-cls="save" color="white" />
                            {{ $t('save') }}
                        </v-btn>
                        <v-btn color="primary" depressed :disabled="saving" @click.stop="goBack">
                            <KpIcon icon-cls="x" color="white" />
                            {{ $t('schliessen') }}
                        </v-btn>

                        <span class="ma-1"> | </span>

                        <v-btn v-if="!record.is_published" color="primary" depressed @click="publishDlg = true">
                            <KpIcon icon-cls="upload" color="white" left />
                            {{ $t('publizieren') }}
                        </v-btn>
                        <v-btn v-if="record.is_published" color="primary" depressed @click="unpublishDlg = true">
                            <KpIcon icon-cls="x" color="white" left />
                            {{ $t('unpublizieren') }}
                        </v-btn>
                        <v-btn
                            v-if="!hidePreview"
                            color="primary"
                            class="ml-1"
                            :disabled="!record.id"
                            depressed
                            :href="`${record.frontendUrl}?preview=enable`"
                            target="tgk_preview"
                        >
                            <KpIcon icon-cls="preview" color="white" left />
                            {{ $t('preview') }}
                        </v-btn>
                    </v-slide-group>
                </v-toolbar>
            </div>

            <!-- Main content -->
            <div class="content overflow-auto">
                <v-form v-model="valid">
                    <v-row dense>
                        <v-col cols="12" sm="6">
                            <v-select
                                v-model="record.page_id"
                                :label="$t('page_type')"
                                :items="pageTypes"
                                dense
                                outlined
                            >
                            </v-select>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="record.reihenfolge"
                                type="number"
                                :label="$t('reihenfolge')"
                                dense
                                outlined
                            />
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="12" sm="6">
                            <v-select
                                v-model="record.genre_group_id"
                                :label="$t('genre_group')"
                                :items="genreGroups"
                                item-value="id"
                                item-text="title"
                                dense
                                outlined
                                @input="loadGenres($event)"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select
                                v-model="record.genre_id"
                                :label="$t('genre')"
                                :items="genres.models"
                                item-value="id"
                                item-text="title"
                                dense
                                outlined
                            />
                        </v-col>
                    </v-row>

                    <v-row dense>
                        <v-col cols="12">
                            <v-text-field
                                v-model="record.title"
                                :label="$t('title')"
                                counter="100"
                                :rules="[formRules.required, formRules.maxLength(100)]"
                                dense
                                outlined
                            />
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="12">
                            <content-block-field
                                v-model="record.content"
                                type="html"
                                :record="record"
                                context="service"
                                :init="{
                                    body_id: 'servicePage_get',
                                    body_class: 'service-page mce-editor-body',
                                    toolbar2:
                                        'template | link unlink image media kpDocument | code fullscreen preview | undo redo',
                                }"
                            />
                        </v-col>
                    </v-row>
                </v-form>
            </div>
        </v-card>
        <confirm-dialog
            v-model="unpublishDlg"
            :title="$t('unpublizieren')"
            :message="$t('unpublizieren_sure')"
            @confirm="onUnpublish"
        />
        <publish-dialog
            v-model="publishDlg"
            :title="$t('Publizieren')"
            :publish-from="publishFrom"
            :publish-to="null"
            :loading="saving"
            @cancel="publishDlg = false"
            @publish="onPublish"
        ></publish-dialog>
    </div>
</template>

<script>
import dayjs from 'dayjs';
import { Service, ServiceCollection } from '@/models/Service';
import { GenreCollection } from '@/models/Genre';
import { getRoles } from '@/models/Role';
import { mapActions } from 'vuex';
import { errToStr, setBrowserTitle, shortenString } from '@/helpers/tools';
import { formRules } from '@/helpers/tools';
import ContentBlockField from '@/components/form/content-block-field.vue';
import PublishDialog from '@/components/publish_dialog.vue';
import { globalParam } from '@/helpers/globals';

export default {
    name: 'ServiceEdit',
    components: {
        ContentBlockField,
        PublishDialog,
    },
    data() {
        return {
            record: null,
            valid: true,
            saving: false,
            roles: getRoles(),
            genres: new GenreCollection(),
            publishFrom: dayjs(),
            publishDlg: false,
            unpublishDlg: false,
        };
    },

    computed: {
        genreGroups() {
            return this.$store.state.genre_group.servicePageCollection
                ? this.$store.state.genre_group.servicePageCollection.models
                : [];
        },
        browserTitle() {
            const parts = [];
            if (this.record?.title) {
                parts.push(shortenString(this.record?.title, 30));
            } else {
                parts.push(this.$t('create_entry'));
            }
            parts.push(this.$t('service_page'));
            return parts.filter(Boolean).join(' - ');
        },
        hidePreview() {
            return globalParam('service.hide_preview');
        }
    },

    watch: {
        browserTitle: {
            handler(newValue) {
                setBrowserTitle(newValue);
            },
            immediate: true,
        },
    },

    async beforeDestroy() {
        if (!this.record?.isPhantom()) {
            await this.save();
        }
    },

    created() {
        this.formRules = formRules;
        this.pageTypes = [
            { value: 3, text: this.$t('service_page') },
            { value: 5, text: this.$t('free_page') },
        ];
    },

    mounted() {
        this.loadRecord(this.$route.params.id);
        this.loadGenreGroups();
    },

    methods: {
        ...mapActions(['runAsync']),
        ...mapActions('genre_group', {
            loadGenreGroups: 'load',
        }),
        async loadGenres(genreGroupId) {
            this.genres.setFilter('genre_group_id', genreGroupId || -1);
            await this.genres.query();
        },
        async loadRecord(recId) {
            // create a new record if requested, or load an existing one:
            if (recId === 'new' || !Number(recId)) {
                this.record = new Service({
                    page_id: 3, // 3 = service page
                });
                // premature return in case of new record:
                return;
            }

            // load existing record:
            this.record = null;
            try {
                const collection = new ServiceCollection();
                await this.runAsync(() => collection.query({ id: recId }));
                const record = collection.first();
                if (record) {
                    this.record = record;
                }
                this.loadGenres(this.record ? this.record.genre_group_id : -1);
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            }
        },

        async save() {
            if (!this.record) {
                return;
            }
            this.saving = true;

            try {
                const isNewRec = !this.record.id;
                await this.runAsync(() => this.record.save());
                if (isNewRec) {
                    // when a new record is created, redirect to the new URL with the ID:
                    this.$router.replace({ name: 'service-edit', params: { id: this.record.id } });
                }
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.saving = false;
            }
        },

        goBack() {
            this.$router.push({ name: 'service' });
        },

        async onUnpublish() {
            if (this.record) {
                this.record.set('publish_from', null);
                this.record.set('publish_to', null);
                await this.save();
            }
        },

        async onPublish({ publishFrom, publishTo }) {
            if (this.record) {
                const fromDate = dayjs(publishFrom);
                const toDate = dayjs(publishTo);
                this.record.set('publish_from', fromDate);
                this.record.set('publish_to', toDate);
                await this.save();
                this.publishDlg = null;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.main-grid {
    display: grid;
    grid-template-rows: auto 1fr;
    grid-template-areas:
        'header'
        'content';
}

.top-header {
    grid-area: header;
}
.content {
    grid-area: content;
    padding: 30px;
}
</style>
